import { useState, useEffect, useCallback, useMemo } from 'react';
import { MonthView } from './MonthView';
import { DayEditModal } from './DayEditModal';
import { VacationRangeDialog } from './VacationRangeDialog';
import { Legend } from './Legend';
import { useCalendarData, type CalendarDay } from '../../hooks/useCalendarData';
import { useApp } from '../../context/AppContext';
import { useHolidays } from '../../hooks/useHolidays';
import type { AbsenceType, DayEntry, CreateAbsenceRequestData } from '../../types';
import { useCalculations } from '../../hooks/useCalculations';
import { recalcFutureVacationHours } from '../../utils/calculations';
import { VacationKPICards } from '../common/VacationKPICards';
import { useAbsenceTypes } from '../../hooks/useAbsenceTypes';
import { useAbsenceRequests } from '../../hooks/useAbsenceRequests';

interface YearCalendarProps {
  hideKPICards?: boolean;
}

export function YearCalendar({ hideKPICards = false }: YearCalendarProps = {}) {
  const { months } = useCalendarData();
  const { state, updateEntry, initializeHolidays, getPreviousYearEntries } = useApp();
  const { results } = useCalculations();
  const viewedUserWageType = state.userWageType ?? null;
  const { absenceTypes, vacationTypes, getById: getAbsenceTypeById } = useAbsenceTypes();
  const { createRequest, planRequest, myRequests, loadMyRequests } = useAbsenceRequests();

  useEffect(() => {
    void loadMyRequests();
  }, [loadMyRequests]);

  const plannedAbsenceMap = useMemo(() => {
    const map = new Map<string, { absenceType?: AbsenceType; requestId: string }>();
    for (const req of myRequests) {
      if (req.status !== 'PLANNED') continue;
      const start = new Date(req.startDate);
      const end = new Date(req.endDate);
      const cursor = new Date(start);
      while (cursor <= end) {
        const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        const value: { absenceType?: AbsenceType; requestId: string } = { requestId: req.id };
        const at = req.absenceType ?? (req.absenceTypeId ? getAbsenceTypeById(req.absenceTypeId) : undefined);
        if (at) value.absenceType = at;
        map.set(dateStr, value);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [myRequests, getAbsenceTypeById]);
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [focusedDate, setFocusedDate] = useState<string | null>(null);
  const [vacationRangeOpen, setVacationRangeOpen] = useState(false);
  const [vacationRangeInitialDate, setVacationRangeInitialDate] = useState<string | undefined>();

  const holidays = useHolidays();
  const holidayDates = useMemo(() => holidays.map((h) => h.date), [holidays]);

  // Dynamische Neuberechnung der Urlaubsstunden für zukünftige Tage (§11 BUrlG)
  const previousYearEntries = useMemo(() => getPreviousYearEntries(), [getPreviousYearEntries]);
  const vacationRecalcMap = useMemo(
    () => recalcFutureVacationHours(state.entries, previousYearEntries),
    [state.entries, previousYearEntries]
  );

  // Initialisiere Feiertage beim Laden
  useEffect(() => {
    initializeHolidays();
  }, [state.year, state.settings.federalState, initializeHolidays]);

  // Alle Tage flach als Array für Navigation
  const allDays = useMemo(() => months.flatMap(m => m.days), [months]);

  const navigateDay = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (!focusedDate) {
      const today = new Date().toISOString().split('T')[0];
      const startDate = allDays.find(d => d.dateString === today)?.dateString || allDays[0]?.dateString;
      setFocusedDate(startDate ?? null);
      return;
    }

    const currentIndex = allDays.findIndex(d => d.dateString === focusedDate);
    if (currentIndex === -1) return;

    let newIndex = currentIndex;
    switch (direction) {
      case 'left':
        newIndex = Math.max(0, currentIndex - 1);
        break;
      case 'right':
        newIndex = Math.min(allDays.length - 1, currentIndex + 1);
        break;
      case 'up':
        newIndex = Math.max(0, currentIndex - 7);
        break;
      case 'down':
        newIndex = Math.min(allDays.length - 1, currentIndex + 7);
        break;
    }

    if (newIndex !== currentIndex) {
      setFocusedDate(allDays[newIndex]?.dateString ?? null);
    }
  }, [focusedDate, allDays]);

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedDay) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigateDay('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateDay('right');
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateDay('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          navigateDay('down');
          break;
        case 'Enter':
        case ' ':
          if (focusedDate) {
            e.preventDefault();
            const day = allDays.find(d => d.dateString === focusedDate);
            if (day && !(day.entry?.isCompanyVacation && viewedUserWageType === 'FIXED')) {
              setSelectedDay(day);
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateDay, focusedDate, selectedDay, allDays, viewedUserWageType]);

  const handleDayClick = (day: CalendarDay) => {
    setFocusedDate(day.dateString);
    setSelectedDay(day);
  };

  const handleDayDoubleClick = (day: CalendarDay) => {
    // Schnelleingabe: Toggle zwischen 0 und Standard-Stunden
    const defaultHours = state.settings.hoursPerDayFullTime;
    const currentHours = day.entry?.hours || 0;
    const newHours = currentHours > 0 ? 0 : defaultHours;

    updateEntry({
      date: day.dateString,
      hours: newHours,
      isHoliday: day.entry?.isHoliday || false,
      isVacation: day.entry?.isVacation || false,
      isHalfVacation: day.entry?.isHalfVacation,
      isSickDay: day.entry?.isSickDay || false,
      note: day.entry?.note,
      // Manager-Anmerkung beibehalten
      managerNote: day.entry?.managerNote,
      managerNoteAuthorId: day.entry?.managerNoteAuthorId,
      managerNoteAuthorName: day.entry?.managerNoteAuthorName,
      managerNoteTimestamp: day.entry?.managerNoteTimestamp,
    });
  };

  const handleSave = (entry: DayEntry) => {
    updateEntry(entry);
    setSelectedDay(null);
  };

  const handleClose = () => {
    setSelectedDay(null);
  };

  const handleOpenVacationRange = (initialDate: string) => {
    setSelectedDay(null);
    setVacationRangeInitialDate(initialDate);
    setVacationRangeOpen(true);
  };

  const handleVacationRangeSave = (updatedEntries: Record<string, DayEntry>) => {
    // updatedEntries ist bereits der volle Merge (aus createVacationEntries / removeVacationFromRange).
    // Nur geänderte Entries updaten (Referenzvergleich — unveränderte Entries behalten ihre Referenz).
    for (const [date, entry] of Object.entries(updatedEntries)) {
      if (entry !== state.entries[date]) {
        updateEntry(entry);
      }
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
        Jahresübersicht {state.year}
      </h2>

      {/* Urlaubs-KPIs */}
      {!hideKPICards && (
        <VacationKPICards
          earnedToDate={results.vacationEarnedToDate ?? 0}
          totalEntitlement={results.totalVacationEntitlement}
          vacationDaysTaken={results.vacationDaysTaken}
          remainingVacation={results.remainingVacation}
          carryOverDays={results.carryOverFromPreviousYear}
          carryOverExpired={state.carryOverVacation?.expired}
          compact
        />
      )}

      {/* Kalender-Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {months.map((month) => (
          <MonthView
            key={month.month}
            month={month}
            focusedDate={focusedDate}
            onDayClick={handleDayClick}
            onDayDoubleClick={handleDayDoubleClick}
            viewedUserWageType={viewedUserWageType}
            vacationRecalcMap={vacationRecalcMap}
            absenceTypeById={getAbsenceTypeById}
            plannedAbsenceMap={plannedAbsenceMap}
          />
        ))}
      </div>

      {/* Legende */}
      <Legend absenceTypes={absenceTypes} />

      {/* Bearbeitungs-Modal */}
      {selectedDay && (
        <DayEditModal
          date={selectedDay.date}
          entry={selectedDay.entry}
          entries={state.entries}
          previousYearEntries={previousYearEntries}
          onSave={handleSave}
          onClose={handleClose}
          onOpenVacationRange={handleOpenVacationRange}
          viewedUserWageType={viewedUserWageType}
          remainingVacation={results.remainingVacation}
          vacationRecalcMap={vacationRecalcMap}
          absenceTypes={absenceTypes}
        />
      )}

      {/* Urlaubsblock-Dialog */}
      <VacationRangeDialog
        open={vacationRangeOpen}
        onOpenChange={setVacationRangeOpen}
        initialDate={vacationRangeInitialDate}
        entries={state.entries}
        holidays={holidayDates}
        year={state.year}
        onSave={handleVacationRangeSave}
        remainingVacation={results.remainingVacation}
        totalEntitlement={results.totalVacationEntitlement}
        vacationTypes={vacationTypes}
        onSubmitRequest={async (data: CreateAbsenceRequestData) => {
          await createRequest(data);
        }}
        plannableTypes={absenceTypes.filter((t) => t.active)}
        onPlanRequest={async (data: CreateAbsenceRequestData) => {
          await planRequest(data);
        }}
      />
    </div>
  );
}
